import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {DfpBannerData} from 'src/app/types/DfpBannerData';
import {AdsCssClassEnum, ElementToggleEnum} from 'src/app/enums/CssClassEnums';
import {StorageService} from '../../../services/storage.service';
import {ToolsService} from '../../../services/tools.service';
import {SessionStorageEnum} from '../../../enums/SessionStorageEnum';
import {DfpAd} from '@wwei/dfp/lib/types';
import {SlotRenderEndedEvent} from '@wwei/dfp/lib/events';

@Component({
  selector: 'app-standard-dfp-banner',
  templateUrl: './standard-dfp-banner.component.html',
  styleUrls: ['./standard-dfp-banner.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandardDfpBannerComponent implements OnInit, OnChanges {
  @Input() data: DfpBannerData;
  public currentCssClass: string = ElementToggleEnum.HIDDEN;
  public bannerCssClass: string;
  public aspectRatioCssStyle: any;
  public adConfig: DfpAd;
  private reloadCallback: Function;

  constructor(
    private _change: ChangeDetectorRef,
    private tools: ToolsService,
    private storage: StorageService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.dataValuesAssign();
  }

  ngOnChanges(): void {
    this.dataValuesAssign();
  }

  refreshed(event: SlotRenderEndedEvent): void {
    if (event.isEmpty === false) {
      this.currentCssClass = ElementToggleEnum.ACTIVE;
    } else {
      this.currentCssClass = ElementToggleEnum.HIDDEN;
    }

    setTimeout(() => {
      this._change.markForCheck();
    }, 0);

    if (!this.tools.isNil(this.reloadCallback)) {
      this.reloadCallback(this.currentCssClass);
    }
  }

  getCurrentCssClass(): string {
    return `${this.currentCssClass} ${this.bannerCssClass}`;
  }

  getLabelCurrentCssClass(): string {
    if (this.tools.isEqual(this.data.size, [[1, 1]]) || this.data.showAdLabel === false) {
      return ElementToggleEnum.HIDDEN;
    }
    return this.currentCssClass;
  }

  getCloseCurrentCssClass(): string {
    if (this.tools.isEqual(this.data.size, [[1, 1]]) || this.tools.isNil(this.data.bannerGroup)) {
      return ElementToggleEnum.HIDDEN;
    }
    return this.currentCssClass;
  }

  closeBanner(): void {
    let storageState = this.storage.read(SessionStorageEnum.CLOSEABLEAD);
    if (this.tools.isNil(storageState)) {
      storageState = [];
    }
    storageState.push(this.data.bannerGroup);
    this.storage.save(SessionStorageEnum.CLOSEABLEAD, storageState);
    this.currentCssClass = ElementToggleEnum.HIDDEN;
  }

  private dataValuesAssign(): void {
    this.adConfig = {
      unitPath: this.data.unitName,
      id: this.generateId(),
      size: this.data.size,
      sizeMapping: [
        [
          [1440, 0],
          [
            [950, 117],
            [728, 90],
            [720, 540],
            [640, 480],
            [320, 600],
            [320, 480],
            [1, 1],
          ],
        ],
        [
          [1200, 0],
          [
            [950, 117],
            [720, 540],
            [480, 320],
            [468, 60],
            [640, 480],
            [320, 600],
            [320, 480],
            [1, 1],
          ],
        ],
        [
          [992, 0],
          [
            [720, 540],
            [560, 175],
            [336, 280],
            [320, 240],
            [320, 600],
            [320, 480],
            [1, 1],
          ],
        ],
        [
          [768, 0],
          [
            [950, 117],
            [720, 540],
            [480, 320],
            [468, 60],
            [320, 240],
            [320, 600],
            [320, 480],
            [320, 320],
            [1, 1],
          ],
        ],
        [
          [576, 0],
          [
            [720, 540],
            [560, 175],
            [336, 280],
            [320, 240],
            [320, 600],
            [320, 480],
            [320, 320],
            [1, 1],
          ],
        ],
        [
          [420, 0],
          [
            [560, 175],
            [380, 285],
            [320, 600],
            [320, 320],
            [320, 480],
            [320, 240],
            [300, 250],
            [1, 1],
          ],
        ],
        [
          [340, 0],
          [
            [560, 175],
            [380, 285],
            [320, 600],
            [320, 320],
            [320, 480],
            [320, 240],
            [300, 250],
            [1, 1],
          ],
        ],
        [
          [320, 0],
          [
            [560, 175],
            [380, 285],
            [300, 250],
            [1, 1],
          ],
        ],
        [[0, 0], [[1, 1]]],
      ],
      collapseEmptyDiv: [true, true],
      targeting: this.generateTargeting(),
    };

    this.bannerCssClass = this.data.isFluid ? AdsCssClassEnum.FLUID : AdsCssClassEnum.NORMAL;
    this.reloadCallback = this.data.reloadCallback;
    this.setAspectRatioCssStyle();
  }

  private setAspectRatioCssStyle(): any {
    this.aspectRatioCssStyle = {};
    if (this.data.isFluid) {
      this.aspectRatioCssStyle = {
        'padding-top': `${(this.data.size[0][1] / this.data.size[0][0]) * 100}%`,
      };
    }
  }

  private generateId(): string {
    return `${this.data.unitName.replace(/\//g, '-')}-${this.tools.getRandomInt(1, 99)}`;
  }

  private generateTargeting(): Record<string, string | string[]> {
    const targeting = {};
    if (!this.tools.isNil(this.data.careproviderUnitTypeId)) {
      targeting['categoryId'] = this.data.careproviderUnitTypeId.toString();
    }
    if (!this.tools.isNil(this.data.careServiceTypeId)) {
      targeting['serviceId'] = this.data.careServiceTypeId.toString();
    }
    if (!this.tools.isNil(this.data.symptomId)) {
      targeting['symptomId'] = this.data.symptomId.toString();
    }
    if (!this.tools.isNil(this.data.placeId)) {
      targeting['placeId'] = this.data.placeId.toString();
    }
    if (!this.tools.isNil(this.data.isCustomer)) {
      targeting['isCustomer'] = this.data.isCustomer;
    }
    if (!this.tools.isNil(this.data.pageModule)) {
      targeting['pageModule'] = this.data.pageModule;
    }
    return targeting;
  }
}
